import type { PropsWithChildren, ReactNode } from "react";

import { CopyButton } from "#components/v2/CopyButton";
import { cn } from "#utils";
import { formatInTimeZone } from "date-fns-tz";

import ChangelogIcon from "../../../assets/commit_icon.svg?react";

export interface ChangelogCardProps extends PropsWithChildren {
  timestamp: Date;
  title: ReactNode;
  action?: string;
  actionMsg?: string;
  copyValue?: string;
  selected?: boolean;
  /** Marks the last entry in a changelog list: hides the connector line trailing below the card. */
  isLast?: boolean;
}

export function ChangelogCard({
  timestamp,
  copyValue,
  title,
  action,
  actionMsg = "",
  selected = false,
  isLast = false,
  children,
}: ChangelogCardProps) {
  const eventDate = formatInTimeZone(new Date(timestamp), "UTC", "MMMM dd, yyyy");
  const eventTime = formatInTimeZone(new Date(timestamp), "UTC", "hh:mm aa");

  return (
    <section
      aria-label="Changelog card"
      className="bg-background-primary isolate relative pb-6 pl-3"
    >
      <div
        className={cn(
          "border-outline-decorative absolute left-6 top-2 z-0 border-l",
          isLast ? "bottom-6" : "bottom-0",
        )}
      />

      <div className="relative z-10 mb-2 flex items-center gap-2">
        <ChangelogIcon />
        <span className="text-text-secondary mb-1 font-semibold uppercase">{eventDate}</span>
      </div>

      <article
        aria-selected={selected}
        className={`border-outline-decorative relative z-10 cursor-pointer rounded-sm border-2 p-4 ${
          selected ? "bg-background-tertiary" : "bg-background-primary"
        }`}
      >
        <header className="mb-4 flex items-center justify-between gap-6">
          <div>
            {copyValue ? <CopyButton value={copyValue}>{title}</CopyButton> : <span>{title}</span>}
          </div>
          <span className="whitespace-nowrap text-text-secondary">{eventTime} UTC</span>
        </header>

        <div className="mb-4">{children}</div>

        {action || actionMsg ? (
          <div className="flex items-center gap-2">
            {/* TODO: build chip component: ENG-4500*/}
            {action ? (
              <div className="bg-background-tertiary text-text-primary rounded-sm px-2 py-px font-bold uppercase">
                {action}
              </div>
            ) : null}
            {actionMsg ? <div className="text-text-secondary">{actionMsg}</div> : null}
          </div>
        ) : null}
      </article>
    </section>
  );
}
